/**
 * TrainingPlanPanel
 *
 * Shared, self-contained panel that displays the current week's training plan
 * for a skill level. Reused on the Dashboard and the Training page (/skills).
 *
 * Behavior:
 * - Plans are keyed by level (Beginner/Intermediate/Advanced), one item per week.
 * - Each week has a training phase + weekly skill emphasis plus a list of daily
 *   sessions. Session dates are derived from the season start date + weekday.
 * - Coaches are locked to their assigned group's `group_skill_level`.
 * - Admins can switch between levels via the reused Beginner/Intermediate/Advanced tabs.
 * - The displayed week is the season's computed "current week"; today's session
 *   (if any) is highlighted.
 */

import { useEffect, useMemo, useState } from 'react';
import { restClient } from '../../../api/rest-client';
import type { Group } from '../../../api/rest-client';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import {
  TRAINING_PLAN_LEVELS,
  TRAINING_SESSION_TEXT_FIELDS,
  getTrainingPlanLevelButtonClasses,
  sessionDate,
  formatLongDate,
  type TrainingPlan,
  type TrainingPlanLevel,
  type TrainingPlanSession,
} from '../../../types/trainingPlans';

interface TrainingPlanPanelProps {
  /** The viewer's assigned group name (coach) or the selected group (admin). May be null. */
  coachGroup: string | null;
  /** Admins can switch freely between level tabs; coaches are locked to their level. */
  isAdmin: boolean;
  /** Optional extra classes for the outer container (e.g. grid span on the dashboard). */
  className?: string;
}

function isTrainingLevel(value: string | undefined | null): value is TrainingPlanLevel {
  return value === 'Beginner' || value === 'Intermediate' || value === 'Advanced';
}

/** Local YYYY-MM-DD for "today", to compare against derived session dates. */
function todayKey(): string {
  const d = new Date();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mo}-${day}`;
}

function dateKey(d: Date): string {
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mo}-${day}`;
}

export default function TrainingPlanPanel({ coachGroup, isAdmin, className = '' }: TrainingPlanPanelProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [plans, setPlans] = useState<TrainingPlan[]>([]);
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [activeLevel, setActiveLevel] = useState<TrainingPlanLevel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch groups (for group -> level mapping) and plans in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [groupsData, plansResp] = await Promise.all([
          restClient.getGroups(),
          restClient.getTrainingPlans(),
        ]);
        if (cancelled) return;
        setGroups(groupsData);
        setPlans(plansResp.plans);
        setCurrentWeek(plansResp.current_week);
        setStartDate(plansResp.start_date);
      } catch (err) {
        if (cancelled) return;
        console.error('[TrainingPlanPanel] Failed to load training plans:', err);
        setError('Failed to load the training plan. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Map group name -> its configured skill level
  const groupLevelMap = useMemo(() => {
    const map = new Map<string, TrainingPlanLevel>();
    for (const g of groups) {
      if (isTrainingLevel(g.group_skill_level)) map.set(g.name, g.group_skill_level);
    }
    return map;
  }, [groups]);

  const coachLevel: TrainingPlanLevel | null =
    coachGroup && groupLevelMap.get(coachGroup) ? groupLevelMap.get(coachGroup)! : null;

  // A non-admin coach is locked to a single level ONLY when their group has a
  // configured skill level. Coaches whose group has no level (or no resolvable
  // group) get the same all-levels view as admins.
  const lockedToCoachLevel = !isAdmin && coachLevel != null;

  // Resolve the active level:
  // - Coach with a configured group level: locked to that level.
  // - Admin, or coach whose group has no level: sync to the group's level when
  //   known, otherwise default to Beginner; they can switch tabs afterward.
  useEffect(() => {
    if (lockedToCoachLevel) {
      setActiveLevel(coachLevel);
      return;
    }
    setActiveLevel((prev) => coachLevel ?? prev ?? 'Beginner');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachGroup, coachLevel, isAdmin, lockedToCoachLevel]);

  const currentPlan = useMemo(() => {
    if (!activeLevel || currentWeek == null) return undefined;
    return plans.find((p) => p.level === activeLevel && p.week === currentWeek);
  }, [plans, activeLevel, currentWeek]);

  // Sessions in weekday order (Mon-first) with derived calendar dates.
  const datedSessions = useMemo(() => {
    if (!currentPlan || currentWeek == null || !startDate) return [];
    const withDates = currentPlan.sessions.map((s) => ({
      session: s,
      date: sessionDate(startDate, currentWeek, s.day_of_week),
    }));
    withDates.sort((a, b) => {
      if (a.date && b.date) return a.date.getTime() - b.date.getTime();
      return 0;
    });
    return withDates;
  }, [currentPlan, currentWeek, startDate]);

  const today = todayKey();
  // Show the level switcher for admins and for coaches not locked to a level.
  const canSwitch = !lockedToCoachLevel;

  return (
    <section className={`rounded-xl border border-gray-200 bg-white ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 pb-3">
        <h2 className="text-2xl font-semibold text-primary-900">Training Plan</h2>
        {currentWeek != null && !loading && (
          <span className="text-sm font-medium text-gray-500" aria-label={`Current week ${currentWeek}`}>
            Week {currentWeek}
          </span>
        )}
      </div>

      {/* Level tabs — shown for admins and for coaches whose group has no
          configured skill level. Coaches locked to a group level see no tabs;
          their level is shown as the plan card heading instead. */}
      {!loading && !error && canSwitch && (
        <div className="flex flex-row gap-1.5 px-4 sm:gap-2" role="tablist" aria-label="Training plan level">
          {TRAINING_PLAN_LEVELS.map((level) => {
            const isActive = level === activeLevel;
            return (
              <button
                type="button"
                key={level}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveLevel(level)}
                className={`flex-1 min-h-[2.75rem] rounded-lg border px-2 text-sm font-medium transition-colors ${getTrainingPlanLevelButtonClasses(
                  level,
                  isActive,
                )}`}
              >
                {level}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className="p-4">
        {loading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
            <p className="text-base text-red-700">{error}</p>
          </div>
        )}

        {!loading && !error && !activeLevel && (
          <p className="py-6 text-center text-base text-gray-500">
            Your group doesn't have a skill level set yet. Ask an admin to assign one.
          </p>
        )}

        {!loading && !error && activeLevel && !currentPlan && (
          <p className="py-6 text-center text-base text-gray-500">
            No {activeLevel} training plan set{currentWeek != null ? ` for week ${currentWeek}` : ''} yet.
          </p>
        )}

        {!loading && !error && activeLevel && currentPlan && (
          <article className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <p className="text-sm font-bold uppercase tracking-wide text-gray-500">
              {activeLevel} Training Plan
            </p>

            {currentPlan.training_phase && (
              <h3 className="mt-3 text-xl font-bold leading-tight text-gray-900">
                {currentPlan.training_phase}
              </h3>
            )}
            {currentPlan.skill_emphasis && (
              <p className="mt-1 text-base text-gray-700">
                <span className="font-semibold">Weekly Skill Emphasis: </span>
                {currentPlan.skill_emphasis}
              </p>
            )}

            {datedSessions.length === 0 ? (
              <p className="mt-4 text-base text-gray-500">No sessions scheduled this week.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {datedSessions.map(({ session, date }, idx) => (
                  <SessionCard
                    key={`${session.day_of_week}-${idx}`}
                    session={session}
                    date={date}
                    isToday={!!date && dateKey(date) === today}
                  />
                ))}
              </div>
            )}
          </article>
        )}
      </div>
    </section>
  );
}

interface SessionCardProps {
  session: TrainingPlanSession;
  date: Date | null;
  isToday: boolean;
}

function SessionCard({ session, date, isToday }: SessionCardProps) {
  return (
    <div
      className={`rounded-lg border p-3 sm:p-4 ${
        isToday ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
        <p className="text-base font-bold text-gray-900">
          {date ? formatLongDate(date) : session.day_of_week}
        </p>
        {isToday && (
          <span className="rounded bg-primary-600 px-1.5 py-0.5 text-sm font-semibold text-white">
            Today
          </span>
        )}
      </div>

      <div className="mt-2 space-y-2">
        {TRAINING_SESSION_TEXT_FIELDS.map(({ key, label }) => {
          const value = session[key];
          if (!value) return null;
          return (
            <div key={key}>
              <p className="text-sm font-bold uppercase tracking-wide text-gray-700 underline">{label}</p>
              <p className="whitespace-pre-line text-base text-gray-800">{value}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
